import React, { useState, useEffect, useCallback, useMemo } from 'react';

// Monthly operating expenses, entered by finance on the Mirror. Stored on the Relay
// (mirror_monthly_expenses) through this server's /api/expenses routes.

const CATEGORIES = [
  'Salaries', 'Office & Rent', 'Software & Services', 'Hosting & Infrastructure',
  'Legal & Compliance', 'Accounting', 'Marketing', 'Bank Fees', 'Travel', 'Other'
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

function cyprusMonth() {
  const p = {};
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Nicosia', year: 'numeric', month: '2-digit' })
    .formatToParts(new Date()).forEach(x => { p[x.type] = x.value; });
  return `${p.year}-${p.month}`;
}

function monthLabel(ym) {
  const [y, m] = String(ym).split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

function fmt(cents, currency) {
  const v = (Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cents < 0 ? '-' : ''}${currency === 'USD' ? '$' : '€'}${v}`;
}

// { EUR: cents, USD: cents } -> "€1,000.00 + $250.00"
function fmtTotals(totals) {
  const parts = ['EUR', 'USD'].filter(c => totals[c]).map(c => fmt(totals[c], c));
  return parts.length ? parts.join(' + ') : fmt(0, 'EUR');
}

const EMPTY_FORM = { expense_month: '', category: '', description: '', amount: '', currency: 'EUR', notes: '' };

function MonthlyExpenses({ token, onLogout }) {
  const [expenses, setExpenses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [month, setMonth] = useState(cyprusMonth());
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(() => ({ 'Authorization': `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/expenses', { headers: authHeaders });
      if (res.status === 401 || res.status === 403) { onLogout(); return; }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load expenses');
      setExpenses(data.expenses || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load expenses');
    } finally {
      setLoaded(true);
    }
  }, [authHeaders, onLogout]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Totals per month (and per currency), newest month first; the selected month is always listed
  const months = useMemo(() => {
    const byMonth = {};
    for (const e of expenses) {
      const m = byMonth[e.expense_month] || (byMonth[e.expense_month] = { count: 0, totals: {} });
      m.count += 1;
      m.totals[e.currency] = (m.totals[e.currency] || 0) + Number(e.amount_cents || 0);
    }
    if (!byMonth[month]) byMonth[month] = { count: 0, totals: {} };
    return Object.keys(byMonth).sort().reverse().map(k => ({ month: k, ...byMonth[k] }));
  }, [expenses, month]);

  const monthRows = expenses.filter(e => e.expense_month === month);
  const monthTotals = (months.find(m => m.month === month) || { totals: {} }).totals;

  const openInsert = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, expense_month: month });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      expense_month: e.expense_month,
      category: e.category,
      description: e.description || '',
      amount: (Number(e.amount_cents) / 100).toFixed(2),
      currency: e.currency || 'EUR',
      notes: e.notes || ''
    });
    setFormError('');
    setShowModal(true);
  };

  const save = async () => {
    const amount = parseFloat(String(form.amount).replace(/,/g, ''));
    if (!/^\d{4}-\d{2}$/.test(form.expense_month)) { setFormError('Choose the month.'); return; }
    if (!form.category.trim()) { setFormError('Choose or type a category.'); return; }
    if (!(amount > 0)) { setFormError('Enter an amount greater than 0.'); return; }
    const body = {
      expense_month: form.expense_month,
      category: form.category.trim(),
      description: form.description.trim(),
      amount_cents: Math.round(amount * 100),
      currency: form.currency,
      notes: form.notes.trim()
    };
    setSaving(true);
    setFormError('');
    try {
      const res = await fetch(editing ? `/api/expenses/${editing.id}` : '/api/expenses', {
        method: editing ? 'PUT' : 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save the expense');
      setShowModal(false);
      setMonth(form.expense_month);
      await load();
    } catch (err) {
      setFormError(err.message || 'Could not save the expense');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.category}${e.description ? ' – ' + e.description : ''}" (${fmt(Number(e.amount_cents), e.currency)})?`)) return;
    try {
      const res = await fetch(`/api/expenses/${e.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not delete the expense');
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete the expense');
    }
  };

  const setField = (k) => (ev) => setForm(f => ({ ...f, [k]: ev.target.value }));

  return (
    <div className="pv-expenses">
      <div className="pv-section">
        <div className="pv-section-header">
          <h2 className="pv-section-title">Monthly Expenses</h2>
          <button className="pv-add-btn" onClick={openInsert}>+ Insert Expenses</button>
        </div>

        {error && <div className="pv-error">{error}</div>}

        <div className="pv-expense-month-bar">
          <label>Month</label>
          <input
            type="month"
            className="pv-form-input pv-month-input"
            value={month}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
          />
          <span className="pv-expense-month-total">
            {monthLabel(month)} total: <strong>{fmtTotals(monthTotals)}</strong>
          </span>
        </div>

        {!loaded ? (
          <div className="pv-loading">Loading expenses...</div>
        ) : monthRows.length === 0 ? (
          <div className="pv-empty">No expenses entered for {monthLabel(month)}</div>
        ) : (
          <table className="pv-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Notes</th>
                <th>Entered by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {monthRows.map(e => (
                <tr key={e.id}>
                  <td>{e.category}</td>
                  <td>{e.description || '-'}</td>
                  <td className="pv-amount">{fmt(Number(e.amount_cents), e.currency)}</td>
                  <td className="pv-notes-cell">{e.notes || '-'}</td>
                  <td className="pv-notes-cell">{e.edited_by ? `${e.entered_by || '-'} (edited by ${e.edited_by})` : (e.entered_by || '-')}</td>
                  <td className="pv-row-actions">
                    <button className="pv-action-btn pv-edit-btn" onClick={() => openEdit(e)}>Edit</button>
                    <button className="pv-action-btn pv-delete-btn" onClick={() => remove(e)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pv-section">
        <div className="pv-section-header">
          <h2 className="pv-section-title">All Months</h2>
        </div>
        <table className="pv-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Entries</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {months.map(m => (
              <tr
                key={m.month}
                className={`pv-clickable-row ${m.month === month ? 'pv-row-selected' : ''}`}
                onClick={() => setMonth(m.month)}
              >
                <td>{monthLabel(m.month)}</td>
                <td>{m.count}</td>
                <td className="pv-amount">{fmtTotals(m.totals)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="pv-modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="pv-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? 'Edit Expense' : 'Insert Expenses'}</h3>
            <div className="pv-form-group">
              <label>Month</label>
              <input type="month" className="pv-form-input" value={form.expense_month} onChange={setField('expense_month')} />
            </div>
            <div className="pv-form-group">
              <label>Category</label>
              <input className="pv-form-input" list="pv-expense-categories" value={form.category}
                     onChange={setField('category')} placeholder="Choose or type a category" maxLength={100} />
              <datalist id="pv-expense-categories">
                {CATEGORIES.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="pv-form-group">
              <label>Description</label>
              <input className="pv-form-input" value={form.description} onChange={setField('description')}
                     placeholder="e.g. September payroll" maxLength={1000} />
            </div>
            <div className="pv-form-row">
              <div className="pv-form-group">
                <label>Amount</label>
                <input className="pv-form-input" inputMode="decimal" value={form.amount} onChange={setField('amount')} placeholder="0.00" />
              </div>
              <div className="pv-form-group">
                <label>Currency</label>
                <select className="pv-form-input" value={form.currency} onChange={setField('currency')}>
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
            </div>
            <div className="pv-form-group">
              <label>Notes</label>
              <textarea className="pv-form-input pv-textarea" value={form.notes} onChange={setField('notes')} rows={3} maxLength={2000} />
            </div>
            {formError && <div className="pv-error">{formError}</div>}
            <div className="pv-modal-actions">
              <button className="pv-modal-cancel" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="pv-modal-save" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyExpenses;
